// frontend/shared/src/prices.ts —— 0045 代理加价定价：售价表草稿、前端预检、只提交改动项的 PUT 体、1310 data.items 结构守卫。
// 代理后台「我的售价」（agent-portal A-10）与 Console 代改售价（admin-console C-09）共用；纯函数，便于单测。
// 口径（计划 dynamic-bubbling-balloon §4 / 02 §5）：留空 = 继承上级；生效价 = max(自己, 上级生效价)；不设上限；
// 开卡价 ≥ 0、≤ 2 位小数；充值费率 0 ≤ r < 100、≤ 4 位小数；前端预检「≥ 上级生效价」，服务端 1310 兜底。
import { ApiError } from './api/client';
import type { AgentPriceBelowParentItem, AgentPricesInput, AgentPricesView } from './api/types';
import { cmp, fmtAmount, parseDecimalInput, toMicro, type Micro } from './money';

export const OPEN_PRICE_DP = 2;
export const DEPOSIT_RATE_DP = 4;
const RATE_CEIL: Micro = toMicro('100');

// 输入框草稿：open 以 product_id 为键；空串 = 继承上级
export interface PriceDrafts {
  open: Readonly<Record<number, string>>;
  deposit: string;
}

// '' = 合法（含留空继承）；format = 格式 / 小数位 / 区间不合法；below = 低于上级生效价
export type PriceDraftError = '' | 'format' | 'below';

function microOrNull(v: string): Micro | null {
  try {
    return toMicro(v);
  } catch {
    return null;
  }
}

// 6 位后端串 → 去尾零（最多 dp 位，向零截断；非法值原样返回不抛错）
export function trimDecimal(v: string, dp: number): string {
  try {
    const s = fmtAmount(v, dp, { group: false });
    return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
  } catch {
    return v;
  }
}

// 开卡价展示：「{x.xx} USDT」两位小数截断（开卡价按每张 USDT 收取）
export function fmtOpenPrice(v: string): string {
  try {
    return `${fmtAmount(v, OPEN_PRICE_DP)} USDT`;
  } catch {
    return v;
  }
}

// 充值费率展示：「{r}%」去尾零（≤ 4 位）
export function fmtDepositRate(v: string): string {
  return `${trimDecimal(v, DEPOSIT_RATE_DP)}%`;
}

// 由服务端视图生成草稿：own = null → 空串（继承）；有值 → 开卡价两位小数、充值费率去尾零
export function draftsOf(v: AgentPricesView): PriceDrafts {
  const open: Record<number, string> = {};
  for (const r of v.open) open[r.product_id] = r.own_price === null ? '' : trimFixed(r.own_price, OPEN_PRICE_DP);
  return { open, deposit: v.deposit.own_rate === null ? '' : trimDecimal(v.deposit.own_rate, DEPOSIT_RATE_DP) };
}

function trimFixed(v: string, dp: number): string {
  try {
    return fmtAmount(v, dp, { group: false });
  } catch {
    return v;
  }
}

function checkDraft(raw: string, dp: number, parent: string, ceil: Micro | null): PriceDraftError {
  const s = raw.trim();
  if (s === '') return '';
  const norm = parseDecimalInput(s, dp);
  if (norm === null) return 'format';
  const m = toMicro(norm);
  if (ceil !== null && cmp(m, ceil) >= 0) return 'format';
  const p = microOrNull(parent);
  return p !== null && cmp(m, p) < 0 ? 'below' : '';
}

export function openDraftError(raw: string, parentPrice: string): PriceDraftError {
  return checkDraft(raw, OPEN_PRICE_DP, parentPrice, null);
}

export function depositDraftError(raw: string, parentRate: string): PriceDraftError {
  return checkDraft(raw, DEPOSIT_RATE_DP, parentRate, RATE_CEIL);
}

// 草稿与服务端自填值是否不同（按数值比较：'12' 与 '12.000000' 视为相同；空串 ⇔ null）
function differs(raw: string, own: string | null, dp: number): boolean {
  const s = raw.trim();
  if (s === '') return own !== null;
  if (own === null) return true;
  const norm = parseDecimalInput(s, dp);
  const a = norm === null ? null : microOrNull(norm);
  const b = microOrNull(own);
  return a === null || b === null || a !== b;
}

export interface PriceCheck {
  open: Readonly<Record<number, PriceDraftError>>;
  deposit: PriceDraftError;
  ok: boolean;
}

// 全表预检：只校验改动过的行（未改动的存量自填价即便已低于上级也不阻塞保存其他行——服务端只校验提交项）
export function checkDrafts(v: AgentPricesView, d: PriceDrafts): PriceCheck {
  const open: Record<number, PriceDraftError> = {};
  let ok = true;
  for (const r of v.open) {
    const raw = d.open[r.product_id] ?? '';
    const e = differs(raw, r.own_price, OPEN_PRICE_DP) ? openDraftError(raw, r.parent_price) : '';
    open[r.product_id] = e;
    if (e) ok = false;
  }
  const deposit = differs(d.deposit, v.deposit.own_rate, DEPOSIT_RATE_DP) ? depositDraftError(d.deposit, v.deposit.parent_rate) : '';
  if (deposit) ok = false;
  return { open, deposit, ok };
}

// PUT 体：只带改动项；无改动返回 null（调用方不发请求）。调用前须 checkDrafts(...).ok
export function pricesPatch(v: AgentPricesView, d: PriceDrafts): AgentPricesInput | null {
  const open: Array<{ product_id: number; price: string | null }> = [];
  for (const r of v.open) {
    const raw = d.open[r.product_id] ?? '';
    if (!differs(raw, r.own_price, OPEN_PRICE_DP)) continue;
    open.push({ product_id: r.product_id, price: raw.trim() === '' ? null : parseDecimalInput(raw, OPEN_PRICE_DP) });
  }
  const body: AgentPricesInput = {};
  if (open.length > 0) body.open = open;
  if (differs(d.deposit, v.deposit.own_rate, DEPOSIT_RATE_DP)) body.deposit = d.deposit.trim() === '' ? null : parseDecimalInput(d.deposit, DEPOSIT_RATE_DP);
  return body.open === undefined && body.deposit === undefined ? null : body;
}

function isItem(x: unknown): x is AgentPriceBelowParentItem {
  if (typeof x !== 'object' || x === null) return false;
  const kind: unknown = Reflect.get(x, 'kind');
  return (kind === 'open' || kind === 'deposit') && typeof Reflect.get(x, 'product_id') === 'number' && typeof Reflect.get(x, 'price') === 'string' && typeof Reflect.get(x, 'parent_eff') === 'string';
}

// 1310 PriceBelowParent：data.items 结构守卫（非 1310 或形状不符返回空数组）
export function priceBelowItems(e: unknown): AgentPriceBelowParentItem[] {
  if (!(e instanceof ApiError) || e.code !== 1310 || !Array.isArray(e.data.items)) return [];
  return e.data.items.filter(isItem);
}

// 1310 逐行标红：open 行按 product_id、deposit 行单独；值为上级当前生效价（插值「最低 {parent_eff}」）
export interface BelowMarks {
  open: Readonly<Record<number, string>>;
  deposit: string | null;
}

export function belowMarks(items: readonly AgentPriceBelowParentItem[]): BelowMarks {
  const open: Record<number, string> = {};
  let deposit: string | null = null;
  for (const it of items) {
    if (it.kind === 'deposit') deposit = it.parent_eff;
    else open[it.product_id] = it.parent_eff;
  }
  return { open, deposit };
}
